import axios from 'axios'
import {
  clearActiveResortId,
  getActiveResortId,
  switchActiveResort,
} from '../utils/activeResort'

const baseURL = import.meta.env.VITE_URL
const BACKEND_UNAVAILABLE_EVENT = 'elda:backend-unavailable'
const BACKEND_RECOVERED_EVENT = 'elda:backend-recovered'
const isDev = import.meta.env.DEV

export const api = axios.create({
  baseURL,
  withCredentials: true,
})

let isRefreshing = false
let failedQueue = []
let backendUnavailable = false

function notifyBackendUnavailable(detail = {}) {
  if (backendUnavailable) return
  backendUnavailable = true
  window.dispatchEvent(
    new CustomEvent(BACKEND_UNAVAILABLE_EVENT, { detail }),
  )
}

function notifyBackendRecovered() {
  if (!backendUnavailable) return
  backendUnavailable = false
  window.dispatchEvent(new CustomEvent(BACKEND_RECOVERED_EVENT))
}

// L en-tete ABSENT laisse passer ; present et vide = 400 sur tout, bootstrap compris.
export function addActiveResortHeader(config) {
  const id = getActiveResortId()
  // un appel qui vise explicitement un autre site garde son en-tete
  if (id !== null && !config.headers?.['X-Resort-Id']) {
    config.headers = config.headers || {}
    config.headers['X-Resort-Id'] = String(id)
  }
  return config
}

api.interceptors.request.use(addActiveResortHeader)

// Mapbox va chercher ses tuiles LUI-MEME, hors axios : l intercepteur ne les voit
// pas. Sans en-tete, resolveScope retombe sur le site d attache — getLayerTile
// rend 204 (muet : pas de 403, donc pas de repli) et tracking/neige rendent 403
// toutes les 30 s. A brancher dans chaque `transformRequest` qui credite le back.
// Reutilise le MEME poseur que l intercepteur : un seul ecrivain de X-Resort-Id,
// comme il n y a qu un seul lecteur du stockage.
export function buildApiTileRequest(url) {
  return addActiveResortHeader({ url, credentials: 'include' })
}

// resolveScope refuse l en-tete hors perimetre (403) ou de forme invalide (400)
// AVANT la logique de route, bootstrap compris : sans ce repli, un site actif
// devenu invalide ne se repare qu en vidant le localStorage a la main. Les deux
// refus portent resortIds, qui est la sortie de secours ; les autres 403
// (permission, site vise non actif) ne le portent pas et passent intacts.
// Le rechargement est injecte : jsdom interdit de remplacer window.location.reload.
// Une requete partie AVANT le logout revient APRES la purge : traitee, elle
// re-arme la cle que le logout vient d effacer, et le compte suivant l envoie en
// en-tete. Le logout suspend donc le repli ; le login le reprend, pour les cas ou
// la navigation vers /login n a pas recharge le document.
let recoverySuspended = false

export function suspendResortScopeRecovery() {
  recoverySuspended = true
}

export function resumeResortScopeRecovery() {
  recoverySuspended = false
}

export function handleResortScopeRefusal(
  error,
  reload = () => window.location.reload(),
) {
  if (recoverySuspended) return false
  const status = error?.response?.status
  if (status !== 400 && status !== 403) return false
  const ids = error.response.data?.resortIds
  if (!Array.isArray(ids)) return false

  const stored = getActiveResortId()
  const next = ids.find(id => Number.isInteger(id) && id > 0) ?? null
  // Recharger sans rien changer reboucle a l infini sur le meme refus.
  if (next === null) {
    if (stored === null) return false
    clearActiveResortId()
  } else {
    if (stored === next) return false
    // Le repli EST une bascule : les deux ecritures, pas seulement la cle.
    switchActiveResort(next)
  }
  reload()
  return true
}

const processQueue = (err, token = null) => {
  failedQueue.forEach(prom => {
    if (err) prom.reject(err)
    else prom.resolve(token)
  })
  failedQueue = []
}

api.interceptors.response.use(
  response => {
    notifyBackendRecovered()
    return response
  },
  async error => {
    const originalRequest = error.config
    const status = error?.response?.status

    // Backend unreachable (deploy/restart/network): switch app to maintenance mode.
    if (status === 503 || [502, 504].includes(status) || (!error?.response && !isDev)) {
      notifyBackendUnavailable({
        status: status ?? null,
        message: error?.message || 'Backend unavailable',
      })
    } else {
      notifyBackendRecovered()
    }

    if (handleResortScopeRefusal(error)) {
      return Promise.reject(error)
    }

    if (status !== 401) {
      return Promise.reject(error)
    }

    const isLoginRequest = originalRequest.url?.includes('/users/login')
    const isRefreshRequest = originalRequest.url?.includes(
      '/users/refresh-token',
    )
    const isLogoutRequest = originalRequest.url?.includes('/users/logout')
    const isGetMeRequest = originalRequest.url?.includes('/users/me')
    const isLoginPage = window.location.pathname === '/login'

    if (
      isLoginRequest ||
      isRefreshRequest ||
      isLogoutRequest ||
      isGetMeRequest ||
      isLoginPage
    ) {
      return Promise.reject(error)
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => api(originalRequest))
          .catch(err => Promise.reject(err))
      }

      isRefreshing = true
      return api
        .post('/users/refresh-token', {}, { withCredentials: true })
        .then(() => {
          processQueue(null)
          return api(originalRequest)
        })
        .catch(refreshErr => {
          processQueue(refreshErr, null)
          window.location.href = '/login'
          return Promise.reject(refreshErr)
        })
        .finally(() => {
          isRefreshing = false
        })
    }

    window.location.href = '/login'
    return Promise.reject(error)
  },
)

export default api
export { BACKEND_UNAVAILABLE_EVENT, BACKEND_RECOVERED_EVENT }
